'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import {
  Search,
  ShoppingCart,
  User as UserIcon,
  Bell,
  Menu,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Share2 as ShareIcon,
  Settings,
  Package,
  LogOut,
  Sparkles,
  CheckCircle,
  Clock,
  User,
  Heart,
  Droplet,
  Flame,
  Hexagon,
  Cookie,
  Wheat,
  Utensils,
  Apple,
  Bean,
  Home,
  LayoutGrid,
  MessageSquare,
  BookOpen,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../app/context/CartContext';
import { useAuth } from '../app/context/AuthContext';
import { useLanguage } from '../app/context/LanguageContext';
import Image from 'next/image';

const CATEGORY_ICON_MAP: Record<string, React.ReactNode> = {
  "oil": <Droplet className="w-4 h-4 text-amber" />,
  "ghee": <Flame className="w-4 h-4 text-amber" />,
  "sweetener": <Cookie className="w-4 h-4 text-amber" />,
  "honey": <Hexagon className="w-4 h-4 text-amber" />,
  "snack": <Apple className="w-4 h-4 text-amber" />,
  "spice": <Utensils className="w-4 h-4 text-amber" />,
  "millet": <Wheat className="w-4 h-4 text-amber" />
};

const getCategoryNavIcon = (title: string) => {
  const lower = title.toLowerCase();
  for (const key of Object.keys(CATEGORY_ICON_MAP)) {
    if (lower.includes(key)) return CATEGORY_ICON_MAP[key];
  }
  return <Droplet className="w-4 h-4 text-amber" />;
};

export const NavItem = ({ title, items }: { title: string, items: string[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div
      className="relative group h-full flex items-center"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="hover:text-amber transition-colors flex items-center gap-1.5 py-4 text-sm font-bold uppercase tracking-wide">
        {getCategoryNavIcon(title)} {title} <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-64 bg-white text-deep-green shadow-2xl rounded-b-2xl border-x border-b border-border overflow-hidden z-[100]"
          >
            <div className="p-4 grid gap-1">
              {items.map((item, i) => (
                <Link
                  key={i}
                  href={`/products?query=${encodeURIComponent(item)}`}
                  className="px-4 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-lg text-xs font-bold transition-all flex items-center gap-2 group/item"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-amber scale-0 group-hover/item:scale-100 transition-transform"></div>
                  {item}
                </Link>
              ))}
            </div>
            <div className="bg-surface p-4 flex justify-end items-center border-t border-border/50">
              <Link href={`/products?category=${encodeURIComponent(title)}`} className="text-[10px] font-bold text-mid-green underline hover:text-deep-green">
                View All
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const decodeToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const FALLBACK_PRODUCTS = [
  { id: 1001, name: 'Pure Palm Jaggery (Karupatti)', price: 340, category: 'Sweeteners', slug: 'pure-palm-jaggery-karupatti' },
  { id: 1002, name: 'Wood Pressed Groundnut Oil', price: 450, category: 'Oils & Ghee', slug: 'wood-pressed-groundnut-oil' },
  { id: 1003, name: 'Raw Forest Wild Honey', price: 650, category: 'Sweeteners', slug: 'raw-forest-wild-honey' },
  { id: 1004, name: 'A2 Kangayam Cow Ghee', price: 850, category: 'Oils & Ghee', slug: 'a2-kangayam-cow-ghee' },
  { id: 1005, name: 'Karuppu Kavuni Laddu', price: 200, category: 'Snacks', slug: 'karuppu-kavuni-laddu' },
  { id: 1006, name: 'Organic Turmeric Powder', price: 150, category: 'Spices', slug: 'organic-turmeric-powder' },
  { id: 1007, name: 'Millet Murukku', price: 120, category: 'Snacks', slug: 'millet-murukku' },
  { id: 1008, name: 'Hand-pounded Chilli Powder', price: 180, category: 'Spices', slug: 'hand-pounded-chilli-powder' },
];

const DEFAULT_HIERARCHY = [
  { title: "Oils & Ghee", items: ["Wood Pressed Groundnut Oil", "Wood Pressed Sesame Oil", "Wood Pressed Coconut Oil", "A2 Kangayam Cow Ghee"] },
  { title: "Sweeteners", items: ["Pure Palm Jaggery (Karupatti)", "Organic Jaggery Powder", "Raw Forest Honey", "Dates Syrup"] },
  { title: "Snacks", items: ["Karuppu Kavuni Laddu", "Millet Murukku", "Palm Jaggery Adhirasam", "Seedai & Thattai"] },
  { title: "Spices", items: ["Organic Turmeric Powder", "Hand-pounded Chilli Powder", "Authentic Sambar Powder", "Rasam Mix"] }
];

export const Navbar = () => {
  const { cart, cartCount, updateQuantity, removeFromCart } = useCart();
  const { token, logout, isAuthenticated } = useAuth();
  const [user, setUser] = useState<any>(null);
  const { language, setLanguage, t } = useLanguage();

  // Active Dropdown Toggles: 'notif' | 'user' | 'cart' | 'lang' | null
  const [activeDropdown, setActiveDropdown] = useState<'notif' | 'user' | 'cart' | 'lang' | null>(null);

  // Maintenance mode checker
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);

  // Languages & Notifications states
  const [enabledLanguages, setEnabledLanguages] = useState<string[]>(['en', 'ta']);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Mobile drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [support, setSupport] = useState<any[]>([
    { title: "Track Order", url: "/orders" },
    { title: "Shipping Policy", url: "/shipping-policy" },
    { title: "Returns & Refunds", url: "/return-policy" },
    { title: "Privacy Policy", url: "/privacy-policy" },
    { title: "Contact Us", url: "/contact-us" }
  ]);

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      const res = await fetch(`${authApi}/api/users/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
        setUnreadCount(data.filter((n: any) => !n.read).length);
      }
    } catch (e) {
      console.warn('Failed to fetch notifications', e);
    }
  };

  const markAllNotificationsRead = async () => {
    if (!token) return;
    try {
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      await fetch(`${authApi}/api/users/notifications/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.warn('Failed to mark notifications read', e);
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  const getNotificationLink = (n: any) => {
    if (n.type === 'ORDER') return `/orders?query=${n.targetId}`;
    if (n.type === 'PRODUCT') return `/product/${n.targetId}`;
    if (n.type === 'BLOG') return `/blog/${n.targetId}`;
    return '#';
  };

  // Scroll and cart triggers for mobile sticky/floating elements
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [showAddedSummary, setShowAddedSummary] = useState(false);
  const [lastCartCount, setLastCartCount] = useState(0);

  // Monitor scroll for floating cart button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setShowFloatingCart(true);
      } else {
        setShowFloatingCart(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Monitor cart additions for slide-up summary
  useEffect(() => {
    if (cartCount > lastCartCount && lastCartCount > 0) {
      setShowAddedSummary(true);
      const timer = setTimeout(() => {
        setShowAddedSummary(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
    setLastCartCount(cartCount);
  }, [cartCount, lastCartCount]);

  const [profileName, setProfileName] = useState('');
  const [profileInitials, setProfileInitials] = useState('U');
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (token) {
      setUser(decodeToken(token));
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      fetch(`${authApi}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setProfile(data);
        if (data && data.firstName) {
          setProfileName(`${data.firstName} ${data.lastName}`.trim());
          setProfileInitials(`${data.firstName.substring(0, 1)}${data.lastName ? data.lastName.substring(0, 1) : ''}`.toUpperCase());
        } else if (data && data.username) {
          setProfileName(data.username);
          setProfileInitials(data.username.substring(0, 2).toUpperCase());
        }
      })
      .catch(() => {});
    } else {
      setUser(null);
      setProfileName('');
      setProfileInitials('U');
      setProfile(null);
    }
  }, [token]);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [blogsList, setBlogsList] = useState<any[]>([]);
  const [categoriesHierarchy, setCategoriesHierarchy] = useState<any[]>(DEFAULT_HIERARCHY);
  const [dbCategories, setDbCategories] = useState<any[]>([]);
  const [hasLoadedSearch, setHasLoadedSearch] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Fetch settings for maintenance mode & categories hierarchy
  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        // Categories hierarchy settings
        if (settings.categories_hierarchy) {
          try {
            setCategoriesHierarchy(JSON.parse(settings.categories_hierarchy));
          } catch (e) {
            console.warn('Error parsing categories_hierarchy configuration JSON', e);
          }
        }
        
        // Support/policy links dynamic mapping
        if (settings.footer_support_links) {
          try {
            const parsed = JSON.parse(settings.footer_support_links);
            const mapped = parsed.map((link: any) => {
              let url = link.url;
              if (url === '#' || !url) {
                const title = link.title.toLowerCase();
                if (title.includes('shipping')) url = '/shipping-policy';
                else if (title.includes('return') || title.includes('refund')) url = '/return-policy';
                else if (title.includes('contact')) url = '/contact-us';
                else if (title.includes('privacy')) url = '/privacy-policy';
              }
              return { ...link, url };
            });
            if (!mapped.some((link: any) => link.title.toLowerCase().includes('privacy'))) {
              mapped.push({ title: "Privacy Policy", url: "/privacy-policy" });
            }
            setSupport(mapped);
          } catch (e) {}
        }
        
        // Enabled languages setting
        if (settings.enabledLanguages) {
          const langs = settings.enabledLanguages.split(',');
          setEnabledLanguages(langs);
          if (!langs.includes(language)) {
            const fallback = langs[0] || 'en';
            setLanguage(fallback as any);
          }
        }

        // Maintenance mode checking
        if (settings.maintenanceMode === 'true') {
          const storedToken = localStorage.getItem('organic_heaven_token');
          const decoded = storedToken ? decodeToken(storedToken) : null;
          if (!decoded || decoded.role !== 'ADMIN') {
            setIsMaintenanceActive(true);
          }
        }
      })
      .catch(err => {
        console.warn('Could not connect to settings API', err);
      });
  }, [pathname]);

  // Load products & blogs lists dynamically on mount
  useEffect(() => {
    const productApi = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';
    
    // Load products list
    fetch(`${productApi}/api/products?size=100`)
      .then(res => res.json())
      .then(data => {
        let fetchedList: any[] = [];
        if (data && data.data && data.data.content) {
          fetchedList = data.data.content;
        } else if (Array.isArray(data)) {
          fetchedList = data;
        }
        
        setProductsList(fetchedList);
      })
      .catch(err => {
        console.warn('Backend connection failed, using offline search fallbacks', err);
      });

    // Load blogs list
    fetch(`${productApi}/api/blogs`)
      .then(res => res.json())
      .then(data => {
        let list = [];
        if (data && data.data) {
          list = data.data;
        } else if (Array.isArray(data)) {
          list = data;
        }
        setBlogsList(list);
      })
      .catch(() => {});

    // Fetch db categories dynamically
    fetch(`${productApi}/api/products/categories`)
      .then(res => res.json())
      .then(resData => {
        if (resData && resData.data) {
          setDbCategories(resData.data);
        } else if (Array.isArray(resData)) {
          setDbCategories(resData);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const query = searchQuery.toLowerCase();
      
      // 1. Products matches
      const matchedProducts = productsList
        .filter(p => p.name.toLowerCase().includes(query))
        .slice(0, 3)
        .map(p => ({
          id: p.id,
          name: p.name,
          price: p.price,
          category: p.category?.name || p.category || 'Organic',
          slug: p.slug,
          type: 'product'
        }));
      
      // 2. Categories matches
      const matchedCategories = categoriesHierarchy
        .filter(c => 
          c.title.toLowerCase().includes(query) || 
          c.items.some((item: string) => item.toLowerCase().includes(query))
        )
        .slice(0, 2)
        .map(c => ({
          id: c.title,
          name: c.title,
          category: 'Category',
          slug: `products?category=${encodeURIComponent(c.title)}`,
          type: 'category'
        }));
      
      // 3. Blogs matches
      const matchedBlogs = blogsList
        .filter(b => b.title.toLowerCase().includes(query))
        .slice(0, 2)
        .map(b => ({
          id: b.id,
          name: b.title,
          category: b.type || 'Article',
          slug: `blog/${b.id}`,
          type: 'blog'
        }));

      setSearchResults([...matchedProducts, ...matchedCategories, ...matchedBlogs]);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, productsList, categoriesHierarchy, blogsList]);

  // Close dropdowns on route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsDrawerOpen(false);
  }, [pathname]);

  // Render maintenance page if enabled
  if (isMaintenanceActive) {
    return (
      <div className="fixed inset-0 bg-[#FAFAF5] z-[9999] flex flex-col items-center justify-center p-6 text-center font-body selection:bg-amber/30 selection:text-deep-green">
        <div className="absolute top-10 left-10 w-72 h-72 bg-mid-green/5 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber/5 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-2xl bg-white p-10 md:p-14 rounded-[2.5rem] border border-border/60 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber via-mid-green to-amber"></div>
          
          <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={96} height={96} className="object-contain mx-auto mb-8 drop-shadow-md" />
          
          <h1 className="text-3xl md:text-4xl font-display font-black text-deep-green mb-4">Nurturing Our Fields</h1>
          <div className="h-[2px] w-32 bg-gradient-to-r from-amber via-mid-green to-transparent rounded-full mx-auto mb-6" />
          
          <p className="text-text-muted font-medium text-base md:text-lg leading-relaxed mb-8">
            Pasumai Virumbi is currently undergoing scheduled maintenance to improve your organic shopping experience. We are working on refreshing our digital fields and will be back shortly.
          </p>
          
          <div className="bg-surface rounded-2xl p-6 border border-border/50 text-left space-y-3 mb-8">
            <span className="block text-xs font-bold text-amber uppercase tracking-wider">Contact support</span>
            <p className="text-sm font-bold text-deep-green">Email: <span className="font-semibold text-text-muted">care@organicheaven.com</span></p>
            <p className="text-sm font-bold text-deep-green">Phone: <span className="font-semibold text-text-muted">+91 98765 43210</span></p>
          </div>
          
          <div className="text-xs text-text-muted font-bold tracking-wider uppercase">
            Thank you for your patience and support!
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop Blur Overlay when a dropdown is active */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/25 backdrop-blur-[2px] z-[40] transition-opacity cursor-pointer"
            onClick={() => setActiveDropdown(null)}
          />
        )}
      </AnimatePresence>

      <header className="w-full bg-white shadow-sm sticky top-0 z-50">
        {/* Top Row */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-3 md:py-4 flex items-center justify-between gap-2 md:gap-4">
          <button 
            onClick={() => setIsDrawerOpen(true)}
            className="md:hidden p-1 text-deep-green hover:text-amber transition-colors mr-1 shrink-0"
            aria-label="Open Menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <Link href="/" className="flex items-center gap-1.5 sm:gap-3 group shrink-0">
            <div className="relative p-1 rounded-full border-2 border-dashed border-amber/80 animate-[spin_20s_linear_infinite] group-hover:animate-[spin_8s_linear_infinite] flex items-center justify-center shrink-0">
              <div className="animate-[spin_20s_linear_infinite_reverse] group-hover:animate-[spin_8s_linear_infinite_reverse] flex items-center justify-center shrink-0">
                <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={42} height={42} className="object-contain transition-transform duration-300 drop-shadow-md sm:w-[52px] sm:h-[52px]" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-base sm:text-xl md:text-2xl font-display font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-deep-green to-mid-green group-hover:from-mid-green group-hover:to-deep-green transition-all duration-500 pb-0.5">
                Pasumai Virumbi
              </span>
              <div className="h-[1.5px] sm:h-[2px] w-3/4 bg-gradient-to-r from-amber to-transparent rounded-full mb-0.5 group-hover:w-full transition-all duration-500" />
              <span className="text-[9px] md:text-[10px] font-bold text-text-muted uppercase tracking-[0.2em] pl-0.5 hidden sm:block">
                100% Pure & Healthy
              </span>
            </div>
          </Link>

          <div className="flex-1 max-w-2xl hidden md:flex flex-col relative z-50">
            <div className="flex-1 flex items-center bg-surface rounded-full px-4 py-2 border border-border focus-within:border-mid-green transition-colors shadow-sm">
              <Search className="text-text-muted w-5 h-5 mr-2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('findProduct')}
                className="w-full bg-transparent border-none focus:ring-0 text-sm py-1 outline-none text-deep-green"
              />
              <button
                onClick={() => {
                  if (searchQuery) router.push(`/products?query=${encodeURIComponent(searchQuery)}`);
                  setSearchQuery('');
                }}
                className="bg-mid-green text-white px-5 py-2 rounded-full text-sm font-bold ml-2 hover:bg-deep-green transition-colors shadow-md"
              >
                {t('search')}
              </button>
            </div>
            
            {/* Search Recommendations Card */}
            {searchQuery.length > 1 && (
              <div className="absolute top-full left-0 w-full mt-2 bg-white rounded-xl shadow-2xl border border-border overflow-hidden">
                <div className="p-3 bg-surface border-b border-border text-left">
                  <span className="text-[10px] font-black text-text-muted uppercase tracking-wider">Top Results</span>
                </div>
                <div>
                  {searchResults.map(res => (
                    <Link 
                      href={res.type === 'category' || res.type === 'blog' ? `/${res.slug}` : `/product/${res.slug}`} 
                      key={`${res.type}-${res.id}`}
                      onClick={() => setSearchQuery('')}
                      className="flex justify-between items-center p-4 hover:bg-pale-green border-b border-border/50 transition-colors group"
                    >
                      <div className="text-left">
                        <h4 className="font-bold text-deep-green group-hover:text-mid-green transition-colors text-sm">{res.name}</h4>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-md mt-1.5 inline-block ${
                          res.type === 'category' ? 'bg-amber/10 text-amber' : res.type === 'blog' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-mid-green'
                        }`}>
                          {res.category}
                        </span>
                      </div>
                      {res.price && <span className="font-bold text-amber">₹{res.price}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-6 text-deep-green">
            <div className="hidden lg:flex items-center gap-4 text-sm font-bold">
              <Link href="/our-story" className="hover:text-amber transition-colors">{t('ourStory')}</Link>
              {isAuthenticated && profile?.foodPreference === 'NON_VEG' && (
                <Link href="/non-veg" className="hover:text-red-500 hover:scale-105 active:scale-95 transition-all text-xs font-black uppercase tracking-wider flex items-center gap-1.5 text-red-700 bg-red-50 border border-red-200 px-3 py-1.5 rounded-full shadow-sm animate-[pulse_2s_infinite]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                  </span>
                  🥩 Fresh Meats & Fish
                </Link>
              )}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-6 md:border-l border-none md:pl-6 pl-0">
              
              {/* Language Selector */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'lang' ? null : 'lang')}
                  className={`hover:text-amber hover:bg-pale-green/60 active:scale-95 transition-all h-11 px-3.5 rounded-full flex items-center gap-1.5 z-50 text-xs font-black uppercase tracking-wider ${activeDropdown === 'lang' ? 'text-amber bg-pale-green/60' : ''}`}
                >
                  <span>
                    {language}
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${activeDropdown === 'lang' ? 'rotate-180' : ''}`} />
                </button>

                <div 
                  className={`absolute top-full right-0 mt-2 w-32 bg-white rounded-xl shadow-2xl border border-mid-green/10 z-50 overflow-hidden flex flex-col transition-all duration-300 ${
                    activeDropdown === 'lang' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="p-1.5 space-y-1 bg-surface">
                    {[
                      { code: 'en', label: 'English' },
                      { code: 'ta', label: 'தமிழ்' },
                      { code: 'hi', label: 'हिन्दी' },
                      { code: 'kn', label: 'ಕನ್ನಡ' },
                      { code: 'ml', label: 'മലയാളം' }
                    ].filter(lang => enabledLanguages.includes(lang.code)).map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code as any);
                          setActiveDropdown(null);
                        }}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                          language === lang.code ? 'bg-pale-green text-mid-green' : 'text-deep-green hover:bg-white'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notifications Dropdown (Premium Green/Gold Theme) */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setActiveDropdown(activeDropdown === 'notif' ? null : 'notif');
                    markAllNotificationsRead();
                  }}
                  className={`hover:text-amber hover:bg-pale-green/60 active:scale-95 transition-all w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center z-50 relative ${activeDropdown === 'notif' ? 'text-amber bg-pale-green/60' : ''}`}
                  aria-label="Notifications"
                >
                  <Bell className="w-5.5 h-5.5" />
                  {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">{unreadCount}</span>}
                </button>

                <div 
                  className={`fixed sm:absolute top-16 sm:top-full left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-80 bg-white rounded-2xl shadow-2xl border border-mid-green/10 z-50 overflow-hidden flex flex-col transition-all duration-300 ${
                    activeDropdown === 'notif' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="p-4 bg-deep-green text-white flex justify-between items-center border-b border-amber/20">
                    <span className="font-display font-bold flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-amber" /> Notifications
                    </span>
                    {unreadCount > 0 && <span className="text-[10px] bg-amber text-deep-green px-2 py-0.5 rounded-full font-bold">{unreadCount} New</span>}
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-border/50">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-xs text-gray-400 font-medium">No notifications yet.</div>
                    ) : (
                      notifications.map(n => (
                        <Link 
                          key={n.id}
                          href={getNotificationLink(n)} 
                          onClick={() => setActiveDropdown(null)} 
                          className={`p-4 hover:bg-pale-green/40 cursor-pointer group flex items-start gap-3 transition-colors text-left block ${!n.read ? 'bg-pale-green/20' : ''}`}
                        >
                          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.read ? 'bg-amber' : 'bg-gray-300'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-deep-green group-hover:text-mid-green truncate">{n.title}</p>
                            <p className="text-xs text-text-muted mt-0.5 whitespace-pre-wrap">{n.message}</p>
                            <span className="text-[9px] text-gray-400 font-bold block mt-1">{new Date(n.createdAt).toLocaleDateString()}</span>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* User Profile Card (Premium Green/Gold Theme with avatar improvements) - Hidden on mobile */}
              <div className="relative hidden md:block">
                {isAuthenticated && user ? (
                  <>
                    <button 
                      onClick={() => setActiveDropdown(activeDropdown === 'user' ? null : 'user')}
                      className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center hover:bg-pale-green/60 active:scale-95 transition-all z-50 cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-full bg-mid-green/10 flex items-center justify-center font-black text-sm text-mid-green border-2 border-mid-green/30 shadow-sm">
                        {profileInitials}
                      </div>
                    </button>
 
                    <div 
                      className={`fixed sm:absolute top-16 sm:top-full left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-64 bg-white rounded-2xl shadow-2xl border border-mid-green/10 z-50 overflow-hidden flex flex-col transition-all duration-300 ${
                        activeDropdown === 'user' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                      }`}
                    >
                      <div className="p-4 bg-deep-green text-white flex items-center gap-3 border-b border-amber/20">
                         <div className="w-10 h-10 bg-amber rounded-full flex items-center justify-center font-bold text-base text-deep-green shadow-inner">
                           {profileInitials}
                         </div>
                         <div className="min-w-0 text-left">
                           <div className="font-bold truncate">{profileName || user.sub || 'User'}</div>
                           <div className="text-[10px] text-white/70 truncate">{user.sub || 'No email'}</div>
                         </div>
                      </div>
                      <div className="p-2 space-y-1 bg-surface">
                         {user.role === 'ADMIN' && (
                           <Link 
                             href="/admin" 
                             onClick={() => setActiveDropdown(null)} 
                             className="flex items-center gap-3 px-4 py-2 hover:bg-white rounded-xl text-sm font-bold text-deep-green transition-colors"
                           >
                             <Settings className="w-4 h-4 text-amber"/> Admin Deck
                           </Link>
                         )}
                         <Link 
                           href="/profile" 
                           onClick={() => setActiveDropdown(null)} 
                           className="flex items-center gap-3 px-4 py-2 hover:bg-white rounded-xl text-sm font-bold text-deep-green transition-colors"
                         >
                           <User className="w-4 h-4 text-amber"/> {t('profile')}
                         </Link>
                         <Link 
                           href="/orders" 
                           onClick={() => setActiveDropdown(null)} 
                           className="flex items-center gap-3 px-4 py-2 hover:bg-white rounded-xl text-sm font-bold text-deep-green transition-colors"
                         >
                           <Package className="w-4 h-4 text-amber"/> {t('orders')}
                         </Link>
                         <div className="h-px bg-border/60 my-1"></div>
                         <button 
                           onClick={() => {
                             setActiveDropdown(null);
                             logout();
                           }} 
                           className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-red-500 rounded-xl text-sm font-bold transition-colors"
                         >
                           <LogOut className="w-4 h-4"/> {t('logout')}
                         </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <Link href="/login" className="bg-mid-green hover:bg-deep-green hover:scale-[1.03] active:scale-95 text-white h-9 px-3 sm:h-11 sm:px-5 rounded-full text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5 uppercase tracking-wider">
                    <UserIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                    <span className="hidden sm:inline">{t('login')}</span>
                  </Link>
                )}
              </div>

              {/* Cart Dropdown (Premium Green/Gold Theme) */}
              <div className="relative">
                <button 
                  onClick={() => setActiveDropdown(activeDropdown === 'cart' ? null : 'cart')}
                  className={`hover:text-amber hover:bg-pale-green/60 active:scale-95 transition-all w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center z-50 relative ${activeDropdown === 'cart' ? 'text-amber bg-pale-green/60' : ''}`}
                  aria-label="Cart"
                >
                  <ShoppingCart className="w-5.5 h-5.5" />
                  {cartCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-amber text-deep-green text-[9px] rounded-full flex items-center justify-center font-bold shadow-sm">{cartCount}</span>
                  )}
                </button>
                
                <div 
                  className={`fixed sm:absolute top-16 sm:top-full left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-96 bg-white rounded-2xl shadow-2xl border border-mid-green/10 z-50 overflow-hidden flex flex-col transition-all duration-300 ${
                    activeDropdown === 'cart' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="p-4 bg-deep-green text-white font-display font-bold flex justify-between items-center border-b border-amber/20 shadow-sm">
                    <span className="text-lg flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-amber" /> {t('cart')}
                    </span>
                    <span className="text-xs bg-white/10 text-amber border border-amber/20 px-2.5 py-1 rounded-full">{cartCount} {language === 'ta' ? 'பொருட்கள்' : 'items'}</span>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto p-4 space-y-4">
                    {cart.length === 0 ? (
                      <div className="text-center py-8 text-text-muted font-medium">Your cart is empty.</div>
                    ) : (
                      cart.map(item => (
                        <div key={item.id} className="flex gap-4 items-center border-b border-border/40 pb-3 last:border-0 last:pb-0">
                          <div className="w-16 h-16 bg-surface rounded-xl shrink-0 overflow-hidden relative border border-border/50 shadow-sm">
                            {item.image ? (
                              <Image src={item.image} alt={item.name} fill className="object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-text-muted">No Img</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <h4 className="text-sm font-bold text-deep-green truncate">{item.name}</h4>
                            <p className="text-amber font-bold text-sm mt-0.5">₹{item.price}</p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-5 h-5 rounded bg-surface border border-border flex items-center justify-center hover:bg-pale-green text-mid-green font-bold text-xs">-</button>
                              <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-5 h-5 rounded bg-surface border border-border flex items-center justify-center hover:bg-pale-green text-mid-green font-bold text-xs">+</button>
                            </div>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700 text-[10px] uppercase font-bold underline shrink-0 self-start mt-1">Remove</button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {cart.length > 0 && (
                    <div className="p-5 border-t border-border/60 bg-surface">
                      <div className="flex justify-between items-center mb-4 font-bold text-deep-green">
                        <span>{t('cartSubtotal')}:</span>
                        <span className="text-amber text-xl">₹{cart.reduce((total, item) => total + (item.price * item.quantity), 0)}</span>
                      </div>
                      <Link 
                        href="/checkout" 
                        onClick={() => setActiveDropdown(null)} 
                        className="block w-full bg-mid-green text-white text-center py-3.5 rounded-xl font-bold hover:bg-deep-green transition-all shadow-md hover:shadow-lg uppercase tracking-wider text-xs"
                      >
                        {t('secureCheckout')}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Mobile Search Row (Fills issues in header hierarchy & adds instant mobile searching) */}
        <div className="px-4 pb-3.5 pt-0.5 md:hidden block relative border-b border-border/40">
          <div className="flex items-center bg-surface rounded-full px-4 py-2 border border-border focus-within:border-mid-green transition-all shadow-sm">
            <Search className="text-text-muted w-4 h-4 mr-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('findProduct')}
              className="w-full bg-transparent border-none text-xs outline-none text-deep-green py-0.5"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-text-muted p-1 hover:text-deep-green transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Mobile autocomplete recommendations dropdown */}
          {searchQuery.length > 1 && (
            <div className="absolute top-full left-4 right-4 mt-1.5 bg-white rounded-2xl shadow-2xl border border-border/80 overflow-hidden z-[100] max-h-72 overflow-y-auto">
              <div className="p-2 bg-surface border-b border-border/40 text-left">
                <span className="text-[9px] font-black text-text-muted uppercase tracking-wider">Search Results</span>
              </div>
              <div className="divide-y divide-border/30">
                {searchResults.map(res => (
                  <Link 
                    href={res.type === 'category' || res.type === 'blog' ? `/${res.slug}` : `/product/${res.slug}`} 
                    key={`${res.type}-${res.id}`}
                    onClick={() => setSearchQuery('')}
                    className="flex justify-between items-center p-3 hover:bg-pale-green/60 transition-colors group text-left block"
                  >
                    <div>
                      <h4 className="font-bold text-deep-green text-xs group-hover:text-mid-green transition-colors">{res.name}</h4>
                      <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md mt-1 inline-block ${
                        res.type === 'category' ? 'bg-amber/10 text-amber' : res.type === 'blog' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-mid-green'
                      }`}>
                        {res.category}
                      </span>
                    </div>
                    {res.price && <span className="font-bold text-amber text-xs">₹{res.price}</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>


        {/* Bottom Ribbon Navigation (Loaded dynamically from Settings) */}
        {pathname !== '/products' && (
          <div className="hidden md:block bg-deep-green text-white shadow-inner border-t border-white/5">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center h-14 justify-between">
              <div className="flex items-center h-full">
                <Link href="/products" className="flex items-center gap-2 bg-amber text-deep-green px-6 h-full font-bold hover:bg-white transition-colors cursor-pointer group">
                  <Menu className="w-5 h-5 group-hover:scale-110 transition-transform" /> All Categories
                </Link>
                <nav className="hidden md:flex items-center gap-8 ml-8 h-full">
                  {dbCategories.filter(cat => {
                    const categoryProducts = productsList.filter(p => (p.categoryName || p.category?.name || '') === cat.name);
                    const hasVeg = categoryProducts.some(p => p.foodPreference !== 'NON_VEG');
                    return categoryProducts.length > 0 && hasVeg;
                  }).map((cat, i) => {
                    const items = productsList
                      .filter(p => (p.categoryName || p.category?.name || '') === cat.name && p.foodPreference !== 'NON_VEG')
                      .map(p => p.name);
                    return (
                      <NavItem key={i} title={cat.name} items={items} />
                    );
                  })}
                  <Link href="/blogs" className="text-white hover:text-amber font-bold text-sm transition-colors flex items-center gap-1">
                    📝 Blogs & Recipes
                  </Link>
                </nav>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Sticky Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border/60 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] h-16 flex justify-around items-center md:hidden z-[99] pb-safe">
        {[
          { name: t('home'), url: '/', icon: Home },
          { name: t('products'), url: '/products', icon: LayoutGrid },
          { name: t('blogs'), url: '/blogs', icon: BookOpen },
          { name: t('profile'), url: '/profile', icon: User }
        ].map((item) => {
          const isActive = pathname === item.url || (item.url !== '/' && pathname.startsWith(item.url));
          const Icon = item.icon;
          return (
            <Link 
              key={item.name} 
              href={item.url} 
              className={`flex flex-col items-center justify-center gap-1 flex-1 h-full py-1 transition-colors ${
                isActive ? 'text-mid-green font-bold' : 'text-text-muted hover:text-deep-green'
              }`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'scale-110 text-mid-green' : 'text-text-muted'} transition-transform`} />
              <span className="text-[8px] tracking-wider uppercase font-black whitespace-nowrap truncate max-w-full px-1 text-center">{item.name}</span>
            </Link>
          );
        })}
      </div>

      {/* Floating mobile cart button */}
      <AnimatePresence>
        {showFloatingCart && !showAddedSummary && cartCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed bottom-20 right-4 z-[98] md:hidden"
          >
            <button
              onClick={() => setActiveDropdown(activeDropdown === 'cart' ? null : 'cart')}
              className="w-12 h-12 rounded-full bg-mid-green text-white shadow-xl hover:bg-deep-green transition-colors border border-white/10 flex items-center justify-center relative shadow-mid-green/30"
              aria-label="Floating Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber text-deep-green font-black text-[10px] flex items-center justify-center border border-white shadow-sm">
                {cartCount}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Premium Slide-up Cart Summary */}
      <AnimatePresence>
        {showAddedSummary && cart.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-20 left-4 right-4 bg-deep-green text-white p-3.5 rounded-2xl border border-amber/30 shadow-2xl flex items-center justify-between z-[98] md:hidden text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber/10 border border-amber/30 flex items-center justify-center text-amber shrink-0">
                <ShoppingCart className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="text-xs font-black uppercase text-amber tracking-wider block leading-none mb-0.5">Item Added!</span>
                <span className="text-[11px] font-bold text-white/95 truncate block">{cartCount} items • Subtotal: ₹{cart.reduce((t, i) => t + (i.price * i.quantity), 0)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowAddedSummary(false)} 
                className="text-white/60 hover:text-white p-1"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <Link 
                href="/checkout" 
                className="bg-amber text-deep-green px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-white hover:text-deep-green transition-colors shadow-md shrink-0"
              >
                Checkout
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Drawer (70% width, sliding from left) */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Backdrop overlay (remaining 30% space outside) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDrawerOpen(false)}
              className="fixed inset-0 bg-black/60 z-[200] md:hidden cursor-pointer"
            />
            
            {/* Drawer container (occupies 70% of horizontal width) */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 bottom-0 left-0 w-[70vw] max-w-[320px] bg-[#FAF9F5] z-[201] md:hidden shadow-2xl flex flex-col justify-between overflow-hidden border-r border-border/80"
            >
              {/* Drawer Header */}
              <div className="p-4 bg-deep-green text-white flex justify-between items-center border-b border-amber/20 shrink-0">
                <div className="flex items-center gap-2">
                  <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={32} height={32} />
                  <span className="font-display font-black text-sm tracking-tight">Pasumai Virumbi</span>
                </div>
                <button 
                  onClick={() => setIsDrawerOpen(false)}
                  className="p-1 rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label="Close Menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-grow overflow-y-auto p-4 space-y-6 text-left">
                {/* Main Navigation */}
                <div className="space-y-1.5">
                  <span className="block text-[10px] font-black text-text-muted uppercase tracking-widest mb-2">Navigation</span>
                  <Link 
                    href="/" 
                    onClick={() => setIsDrawerOpen(false)}
                    className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-xl text-xs font-bold text-deep-green transition-colors block"
                  >
                    🏠 Home
                  </Link>
                  <Link 
                    href="/products" 
                    onClick={() => setIsDrawerOpen(false)}
                    className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-xl text-xs font-bold text-deep-green transition-colors block"
                  >
                    📦 Browse Products
                  </Link>
                  <Link 
                    href="/blogs" 
                    onClick={() => setIsDrawerOpen(false)}
                    className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-xl text-xs font-bold text-deep-green transition-colors block"
                  >
                    📝 Blogs & Recipes
                  </Link>
                  <Link 
                    href="/our-story" 
                    onClick={() => setIsDrawerOpen(false)}
                    className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-xl text-xs font-bold text-deep-green transition-colors block"
                  >
                    🌾 Our Story
                  </Link>
                </div>

                {/* Categories */}
                <div className="space-y-2">
                  <span className="block text-[10px] font-black text-text-muted uppercase tracking-widest mb-1">Categories</span>
                  {categoriesHierarchy.map((cat, idx) => (
                    <div key={idx} className="space-y-1 pl-1">
                      <span className="block text-[11px] font-black text-deep-green uppercase tracking-wide px-2.5 py-1">{cat.title}</span>
                      <div className="grid grid-cols-1 gap-0.5 pl-2 border-l border-border/80 ml-2">
                        {cat.items?.map((item: string, i: number) => (
                          <Link 
                            key={i}
                            href={`/products?query=${encodeURIComponent(item)}`}
                            onClick={() => setIsDrawerOpen(false)}
                            className="px-2.5 py-1.5 text-[11px] font-semibold text-text-muted hover:text-mid-green rounded-lg transition-colors block"
                          >
                            • {item}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Support & Legal Links */}
                <div className="space-y-1.5">
                  <span className="block text-[10px] font-black text-text-muted uppercase tracking-widest mb-2">Support & Info</span>
                  {support.map((link, idx) => (
                    <Link 
                      key={idx}
                      href={link.url}
                      onClick={() => setIsDrawerOpen(false)}
                      className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-pale-green hover:text-mid-green rounded-xl text-xs font-bold text-deep-green transition-colors block"
                    >
                      📎 {link.title}
                    </Link>
                  ))}
                </div>

                {/* Language (Mobile Selector) */}
                <div className="space-y-2 pt-4 border-t border-border/60">
                  <span className="block text-[10px] font-black text-text-muted uppercase tracking-widest">Select Language</span>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { code: 'en', label: 'English' },
                      { code: 'ta', label: 'தமிழ்' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code as any);
                          setIsDrawerOpen(false);
                        }}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all text-center border ${
                          language === lang.code 
                            ? 'bg-deep-green border-deep-green text-white font-bold' 
                            : 'bg-white border-border text-deep-green hover:border-mid-green'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drawer Footer (User Profile / Logout) */}
              <div className="p-4 bg-surface border-t border-border/80 text-left shrink-0">
                {isAuthenticated && user ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber text-deep-green flex items-center justify-center font-bold text-xs shrink-0">
                        {user.sub ? user.sub.substring(0, 2).toUpperCase() : 'U'}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-xs text-deep-green truncate">{user.sub || 'User'}</div>
                        <div className="text-[9px] text-text-muted truncate">{user.email || 'No email'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link 
                        href="/profile" 
                        onClick={() => setIsDrawerOpen(false)}
                        className="py-2 bg-white border border-border text-deep-green text-center rounded-xl text-[10px] font-bold uppercase tracking-wider block"
                      >
                        Profile
                      </Link>
                      <button 
                        onClick={() => {
                          setIsDrawerOpen(false);
                          logout();
                        }}
                        className="py-2 bg-red-50 text-red-500 text-center rounded-xl text-[10px] font-bold uppercase tracking-wider block"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link 
                    href="/login" 
                    onClick={() => setIsDrawerOpen(false)}
                    className="block w-full bg-mid-green hover:bg-deep-green text-white text-center py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm"
                  >
                    Login / Register
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const renderSocialIcon = (platform: string) => {
  const p = platform.toLowerCase();
  if (p.includes('instagram')) {
    return (
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    );
  }
  if (p.includes('facebook')) {
    return (
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }
  if (p.includes('twitter')) {
    return (
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
      </svg>
    );
  }
  if (p.includes('youtube')) {
    return (
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M23.498 6.163a3.003 3.003 0 00-2.11-2.11C19.517 3.545 12 3.545 12 3.545s-7.517 0-9.388.507a3.003 3.003 0 00-2.11 2.11C0 8.033 0 12 0 12s0 3.967.502 5.837a3.003 3.003 0 002.11 2.11c1.871.507 9.388.507 9.388.507s7.517 0 9.388-.507a3.003 3.003 0 002.11-2.11C24 15.967 24 12 24 12s0-3.967-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    );
  }
  return <ShareIcon className="w-4 h-4" />;
};

export const Footer = () => {
  const [desc, setDesc] = useState("Bringing the purity of nature directly from our certified farms to your dining table. Join the organic revolution today.");
  const [socials, setSocials] = useState<any[]>([
    { platform: "Instagram", url: "https://instagram.com" },
    { platform: "Facebook", url: "https://facebook.com" },
    { platform: "Twitter", url: "https://twitter.com" }
  ]);
  const [support, setSupport] = useState<any[]>([
    { title: "Track Order", url: "/orders" },
    { title: "Shipping Policy", url: "/shipping-policy" },
    { title: "Returns & Refunds", url: "/return-policy" },
    { title: "Privacy Policy", url: "/privacy-policy" },
    { title: "Contact Us", url: "/contact-us" }
  ]);
  const [contact, setContact] = useState<any>({
    address: "123 Organic Lane, Green Valley, Erode, Tamil Nadu, 638001",
    phone: "+91 98765 43210",
    email: "care@organicheaven.com"
  });

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.footer_description) setDesc(settings.footer_description);
        if (settings.footer_social_links) {
          try { setSocials(JSON.parse(settings.footer_social_links)); } catch (e) {}
        }
        if (settings.footer_support_links) {
          try {
            const parsed = JSON.parse(settings.footer_support_links);
            const mapped = parsed.map((link: any) => {
              let url = link.url;
              if (url === '#' || !url) {
                const title = link.title.toLowerCase();
                if (title.includes('shipping')) url = '/shipping-policy';
                else if (title.includes('return') || title.includes('refund')) url = '/return-policy';
                else if (title.includes('contact')) url = '/contact-us';
                else if (title.includes('privacy')) url = '/privacy-policy';
              }
              return { ...link, url };
            });
            if (!mapped.some((link: any) => link.title.toLowerCase().includes('privacy'))) {
              mapped.push({ title: "Privacy Policy", url: "/privacy-policy" });
            }
            setSupport(mapped);
          } catch (e) {}
        }
        if (settings.footer_contact_details) {
          try { setContact(JSON.parse(settings.footer_contact_details)); } catch (e) {}
        }
      })
      .catch(err => console.warn("Failed to load footer settings", err));
  }, []);

  return (
    <footer className="hidden md:block bg-deep-green text-white/80 pt-20 pb-8 relative z-10 border-t border-white/5">
      <div className="hidden md:grid max-w-[1400px] mx-auto px-6 md:px-12 grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16 border-b border-white/10 pb-16">
        <div className="lg:col-span-4 text-left">
          <div className="flex items-center gap-3 mb-6">
            <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={56} height={56} className="object-contain" />
            <span className="text-3xl font-display font-black text-white">Pasumai Virumbi</span>
          </div>
          <p className="mb-8 leading-relaxed text-sm text-white/70 max-w-sm">{desc}</p>
          <div className="flex gap-4">
            {socials.map((soc, idx) => {
              return (
                <a 
                  key={idx} 
                  href={soc.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber hover:text-deep-green transition-all shadow-md"
                >
                  {renderSocialIcon(soc.platform)}
                </a>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 text-left">
          <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Shop Categories</h4>
          <ul className="space-y-4 text-sm font-semibold">
            <li><Link href="/products?category=Oils%20%26%20Ghee" className="hover:text-amber transition-colors">Oils & Ghee</Link></li>
            <li><Link href="/products?category=Sweeteners" className="hover:text-amber transition-colors">Sweeteners</Link></li>
            <li><Link href="/products?category=Snacks" className="hover:text-amber transition-colors">Traditional Snacks</Link></li>
            <li><Link href="/products?category=Spices" className="hover:text-amber transition-colors">Organic Spices</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2 text-left">
          <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Support</h4>
          <ul className="space-y-4 text-sm font-semibold">
            {support.map((link, idx) => (
              <li key={idx}>
                <Link href={link.url} className="hover:text-amber transition-colors">{link.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4 text-left">
          <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Contact Us</h4>
          <div className="space-y-4 text-sm font-semibold">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber shrink-0 mt-0.5" />
              <p className="text-white/70">{contact.address}</p>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-amber shrink-0" />
              <p className="text-white/70">{contact.phone}</p>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-amber shrink-0" />
              <p className="text-white/70">{contact.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm text-white/50 text-center md:text-left">
        <p>© 2026 Pasumai Virumbi. All rights reserved.</p>
        <div className="flex gap-4 hidden sm:block">
          <img src="/images/payment-icons.png" alt="Payments" className="h-8 w-auto object-contain opacity-40 grayscale hover:grayscale-0 transition-all" />
        </div>
      </div>
    </footer>
  );
};
